import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'antd';
import { FiSearch, FiUsers, FiMoreHorizontal, FiEdit2, FiCopy, FiDownload, FiTrash2, FiUserPlus, FiEdit3 } from 'react-icons/fi';
import { Button, TextField, Card } from '../../design-system';
import { Menu, MenuTrigger, MenuContent, MenuItem, MenuSeparator } from '../../design-system/menu';
import { getProjects, exportProjectPackage, removeUserFromProject } from '../../api/data';
import { useAppContext } from '../../providers/AppContext';
import { useSelected } from '../../providers/selectedContext';
import { useAuth } from '../../providers/logincontext';
import { Project } from '../../@types/projects/types';
import ProjectModal from '../../components/modals/projectModal';
import ImportProjectModal from '../../components/modals/ImportProjectModal';
import DuplicateProjectModal from '../../components/modals/DuplicateProjectModal';
import { EditProjectModal, DeleteConfirmModal, AddMemberModal, EditMemberModal } from '../../design-system';
import { deleteProject } from '../../api/data';
import styles from './ProjectsPage.module.css';
import emptyStateIllustration from '../../assets/empty-state.svg';
import SkeletonPage from '../../components/skeleton/SkeletonPage';
import { User } from '../../@types/users/user';


type IconProps = { size?: number; color?: string };
const SearchIcon = FiSearch as React.FC<IconProps>;
const MembersIcon = FiUsers as React.FC<IconProps>;
const MoreIcon = FiMoreHorizontal as React.FC<IconProps>;
const EditIcon = FiEdit2 as React.FC<IconProps>;
const CopyIcon = FiCopy as React.FC<IconProps>;
const DownloadIcon = FiDownload as React.FC<IconProps>;
const TrashIcon = FiTrash2 as React.FC<IconProps>;
const AddMemberIcon = FiUserPlus as React.FC<IconProps>;
const RoleIcon = FiEdit3 as React.FC<IconProps>;

type ProjectCardProps = {
  project: Project;
  canExport: boolean;
  canManageMembers: boolean;
  currentUserUid?: string;
  onClick: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onExport: () => void;
  onDelete: () => void;
  onAddMember: (project: Project) => void;
  onEditMember: (project: Project, user: User) => void;
  onRemoveMember: (project: Project, user: User) => void;
};

const ProjectCard = ({
  project,
  canExport,
  canManageMembers,
  currentUserUid,
  onClick,
  onEdit,
  onDuplicate,
  onExport,
  onDelete,
  onAddMember,
  onEditMember,
  onRemoveMember
}: ProjectCardProps) => {
  const meta: { label: string; value: string | null | undefined }[] = [
    { label: 'Country', value: project.country },
    { label: 'Block', value: project.block },
    { label: 'Reservoir', value: project.reservoir },
    { label: 'Rock type', value: project.rock_type },
  ].filter(m => m.value);
  const members = project.users ?? [];

  return (
    <Card variant="default" padding="none" interactive fullWidth onClick={onClick}>
      <Card.Header
        title={project.project_name ?? '—'}
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className={styles.membersTag}>
              <MembersIcon size={12} color="#8c8c8c" />
              {project.users?.length ?? 0}
            </span>
            <Menu>
              <MenuTrigger>
                <button
                  className={styles.menuButton}
                  aria-label="Project actions"
                  onClick={e => e.stopPropagation()}
                >
                  <MoreIcon size={14} color="#8c8c8c" />
                </button>
              </MenuTrigger>
              <MenuContent align="end">
                <MenuItem
                  icon={<EditIcon size={13} />}
                  onClick={e => { e.stopPropagation(); onEdit(); }}
                >
                  Edit
                </MenuItem>
                <MenuItem
                  icon={<CopyIcon size={13} />}
                  onClick={e => { e.stopPropagation(); onDuplicate(); }}
                >
                  Duplicate
                </MenuItem>
                {canExport && (
                  <MenuItem
                    icon={<DownloadIcon size={13} />}
                    onClick={e => { e.stopPropagation(); onExport(); }}
                  >
                    Export
                  </MenuItem>
                )}
                <MenuSeparator />
                <MenuItem
                  icon={<TrashIcon size={13} />}
                  variant="destructive"
                  onClick={e => { e.stopPropagation(); onDelete(); }}
                >
                  Delete
                </MenuItem>
              </MenuContent>
            </Menu>
          </div>
        }
      />
      {meta.length > 0 && (
        <Card.Body>
          <ul className={styles.metaList}>
            {meta.map(({ label, value }) => (
              <li key={label} className={styles.metaItem}>
                <span className={styles.metaLabel}>{label}</span>
                <span className={styles.metaValue}>{value}</span>
              </li>
            ))}
          </ul>
        </Card.Body>
      )}
      <div className={styles.membersSection}>
        <div className={styles.membersHeader}>
          <div className={styles.membersTitleRow}>
            <span className={styles.membersSectionTitle}>Members</span>
            <span className={styles.membersSectionCount}>{members.length}</span>
          </div>
          {canManageMembers && (
            <Button
              variant="ghost"
              size="sm"
              startIcon={<AddMemberIcon size={13} />}
              onClick={event => {
                event.stopPropagation();
                onAddMember(project);
              }}
            >
              Add member
            </Button>
          )}
        </div>
        <ul className={styles.memberList}>
          {members.map(user => {
            const showActions = canManageMembers && currentUserUid !== user.uid && user.role !== 'owner';
            return (
              <li
                key={user.uid}
                className={styles.memberRow}
                onClick={event => event.stopPropagation()}
              >
                <div className={styles.memberInfo}>
                  <span className={styles.memberEmail}>{user.email}</span>
                  <span className={`${styles.memberRole} ${user.role === 'owner' ? styles.memberRoleOwner : ''}`}>
                    {user.role}
                  </span>
                </div>
                {showActions ? (
                  <div className={styles.memberActions}>
                    <Button
                      variant="ghost"
                      size="sm"
                      startIcon={<RoleIcon size={12} />}
                      onClick={event => {
                        event.stopPropagation();
                        onEditMember(project, user);
                      }}
                    >
                      Role
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      startIcon={<TrashIcon size={12} />}
                      onClick={event => {
                        event.stopPropagation();
                        onRemoveMember(project, user);
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    </Card>
  );
};

const isProjectOwner = (project: Project, userUid?: string) => {
  if (!userUid || !project.users?.length) return false;
  return (project.users as any[]).some(u => u.uid === userUid && u.role === 'owner');
};

const buildProjectExportFilename = (projectName?: string | null) => {
  const baseName = (projectName || 'project_export').trim() || 'project_export';
  const safeName = baseName.replace(/[^A-Za-z0-9_.-]+/g, '_');
  return `${safeName}.json`;
};

const ProjectsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tokenExpired, setTokenExpired] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [editMemberModalOpen, setEditMemberModalOpen] = useState(false);
  const [memberProject, setMemberProject] = useState<Project | null>(null);
  const [memberTarget, setMemberTarget] = useState<User | null>(null);
  const [removeMemberModalOpen, setRemoveMemberModalOpen] = useState(false);
  const [isRemovingMember, setIsRemovingMember] = useState(false);
  const { currentUser, logout } = useAuth();
  const { selectedInfo, setSelectedInfo } = useSelected();
  const { configStates, updateConfigStates, data, updateData, upsertProject } = useAppContext();

  const projects: Project[] = Array.isArray(data.projects) ? data.projects : [];

  const loadProjects = async () => {
    setLoading(true);
    try {
      const response = await getProjects();
      updateData({ projects: response });
      const currentUid = selectedInfo.project?.uid;
      if (currentUid) {
        const freshProject = response.find(p => p.uid === currentUid);
        if (freshProject) setSelectedInfo({ project: freshProject });
      }
    } catch (error: any) {
      if (error?.response?.status === 401) {
        setTokenExpired(true);
      } else {
        console.error('Error loading projects:', error);
      }
    } finally {
      setLoading(false);
      updateConfigStates({ fetchProjects: false });
    }
  };

  const handleImportSuccess = (project: Project) => {
    updateData({ projects: [project, ...projects] });
    setSelectedInfo({ project });
    updateConfigStates({ fetchProjects: true });
  };

  const handleDuplicateSuccess = (project: Project) => {
    upsertProject(project);
    setSelectedInfo({ project });
    updateConfigStates({ fetchProjects: true, fetchWells: true, changeProject: true });
    setDuplicateModalOpen(false);
  };

  const handleProjectMutationSuccess = (project: Project) => {
    upsertProject(project);
    if (selectedInfo.project?.uid === project.uid || memberProject?.uid === project.uid) {
      setSelectedInfo({ project });
    }
    setMemberProject(project);
  };

  const openAddMember = (project: Project) => {
    setSelectedInfo({ project });
    setMemberProject(project);
    setAddMemberModalOpen(true);
  };

  const openEditMember = (project: Project, user: User) => {
    setSelectedInfo({ project });
    setMemberProject(project);
    setMemberTarget(user);
    setEditMemberModalOpen(true);
  };

  const openRemoveMember = (project: Project, user: User) => {
    setSelectedInfo({ project });
    setMemberProject(project);
    setMemberTarget(user);
    setRemoveMemberModalOpen(true);
  };

  const closeEditMember = () => {
    setEditMemberModalOpen(false);
    setMemberTarget(null);
  };

  const closeRemoveMember = () => {
    setRemoveMemberModalOpen(false);
    setMemberTarget(null);
  };

  const handleRemoveMember = async () => {
    if (!memberProject || !memberTarget) return;
    setIsRemovingMember(true);
    try {
      const updatedProject = await removeUserFromProject(memberProject.uid, memberTarget.email);
      handleProjectMutationSuccess(updatedProject);
      closeRemoveMember();
    } catch (error) {
      console.error('Error removing member:', error);
    } finally {
      setIsRemovingMember(false);
    }
  };

  const openEdit = (project: Project) => {
    setSelectedInfo({ project });
    setEditModalOpen(true);
  };

  const openDuplicate = (project: Project) => {
    setSelectedInfo({ project });
    setDuplicateModalOpen(true);
  };

  const openDelete = (project: Project) => {
    setDeleteTarget(project);
    setDeleteModalOpen(true);
  };

  const handleDeleteProject = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteProject(deleteTarget.uid);
      updateData({
        projects: data.projects.filter(p => p.uid !== deleteTarget.uid),
        wells: [],
        projectSamples: [],
        wellSamples: [],
      });
      if (selectedInfo.project?.uid === deleteTarget.uid) {
        setSelectedInfo({ project: undefined, well: undefined, sample: undefined });
      }
      setDeleteModalOpen(false);
    } catch (error) {
      console.error('Error deleting project:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExport = async (project: Project) => {
    try {
      const exportedPackage = await exportProjectPackage(project.uid);
      const blob = new Blob([JSON.stringify(exportedPackage.data, null, 2)], {
        type: 'application/json'
      });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download =
        exportedPackage.filename ||
        buildProjectExportFilename(
          exportedPackage.data.project.project_name || project.project_name
        );
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    } catch {
      Modal.error({ title: 'Export failed', content: 'Unable to export project.' });
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (configStates.fetchProjects) {
      loadProjects();
    }
  }, [configStates.fetchProjects]);

  useEffect(() => {
    if (!tokenExpired) return;
    Modal.error({
      title: 'Token expired',
      content: <p>Your token has expired. Please log in again.</p>,
      okText: 'Logout',
      onOk: () => logout(),
    });
  }, [tokenExpired, logout]);

  const filtered = [...projects]
    .sort((a, b) => (a.project_name ?? '').localeCompare(b.project_name ?? ''))
    .filter(p =>
      !search || (p.project_name ?? '').toLowerCase().includes(search.toLowerCase())
    );

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title}>Projects</h1>
          <div className={styles.headerActions}>
            <Button variant="secondary" onClick={() => setImportModalOpen(true)}>
              Import
            </Button>
            <Button variant="primary" onClick={() => updateConfigStates({ showProject: true })}>
              New Project
            </Button>
          </div>
        </div>

        <div className={styles.searchRow}>
          <TextField
            placeholder="Search"
            fullWidth
            value={search}
            onChange={e => setSearch(e.target.value)}
            startAdornment={<SearchIcon size={16} color="#8c8c8c" />}
          />
        </div>

        {loading ? (
          <SkeletonPage hasSidebar={false} />
        ) : filtered.length === 0 ? (
          <div className={styles.emptyState}>
            <img src={emptyStateIllustration} alt="No projects" className={styles.emptyIllustration} />
            <p className={styles.emptyTitle}>No projects yet</p>
            <p className={styles.emptySubtitle}>Projects will appear here once created.</p>
            <Button variant="primary" onClick={() => updateConfigStates({ showProject: true })}>
              New Project
            </Button>
          </div>
        ) : (
          <div className={styles.grid}>
            {filtered.map(project => (
              <ProjectCard
                key={project.uid}
                project={project}
                canExport={isProjectOwner(project, currentUser?.uid)}
                canManageMembers={isProjectOwner(project, currentUser?.uid)}
                currentUserUid={currentUser?.uid}
                onClick={() => {
                  navigate(`/capillary-pressure?project=${project.uid}`);
                }}
                onEdit={() => openEdit(project)}
                onDuplicate={() => openDuplicate(project)}
                onExport={() => handleExport(project)}
                onDelete={() => openDelete(project)}
                onAddMember={openAddMember}
                onEditMember={openEditMember}
                onRemoveMember={openRemoveMember}
              />
            ))}
          </div>
        )}
      </div>
      <ProjectModal />
      <ImportProjectModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={handleImportSuccess}
      />
      <AddMemberModal
        open={addMemberModalOpen}
        onClose={() => setAddMemberModalOpen(false)}
        onSuccess={project => {
          handleProjectMutationSuccess(project);
          setAddMemberModalOpen(false);
        }}
      />
      <EditMemberModal
        open={editMemberModalOpen}
        onClose={closeEditMember}
        onSuccess={project => {
          handleProjectMutationSuccess(project);
          closeEditMember();
        }}
        user={memberTarget}
      />
      <EditProjectModal
        isModalOpen={editModalOpen}
        setIsModalOpen={setEditModalOpen}
      />
      <DuplicateProjectModal
        open={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        onSuccess={handleDuplicateSuccess}
      />
      <DeleteConfirmModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        itemType="project"
        itemName={deleteTarget?.project_name ?? ''}
        loading={isDeleting}
        onConfirm={handleDeleteProject}
      />
      <DeleteConfirmModal
        open={removeMemberModalOpen}
        onClose={closeRemoveMember}
        itemType="member"
        itemName={memberTarget?.email ?? ''}
        loading={isRemovingMember}
        onConfirm={handleRemoveMember}
      />
    </div>
  );
};

export default ProjectsPage;
